// ✅ src/pages/OrderManagement/InternalEstimation.jsx
// FULL FILE - UPDATED WITH LOCALSTORAGE HEADERS
// NOTE: Add flow is kept as-is. Only integrated localStorage helper + commonHeaders in all API calls.

import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaPlus, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const InternalEstimation = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  const branchid = Number(branchId || 0);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const location = useLocation();
  const qp = new URLSearchParams(location.search);

  const mode = (qp.get("mode") || "new").toLowerCase(); // new | view | edit
  const projectidFromUrl = Number(qp.get("projectid") || 0);
  const estimationidFromUrl = Number(qp.get("estimationid") || 0);
  const estDetIdFromUrl = Number(qp.get("estDetId") || 0);
  const isView = mode === "view";
  const isEdit = mode === "edit";
  const isNew = mode === "new";
  const canEditInitially = mode === "edit";

  const [editEnabled, setEditEnabled] = useState(canEditInitially);

  // derived
  const isReadOnly = isView && !editEnabled;
  const isEditingNow = isView && editEnabled;

  // -----------------------------
  // Form state
  // -----------------------------
  const [formData, setFormData] = useState({
    projectId: "",
    projectName: "",
    projectCode: "",

    materialType: "Raw Material",

    itemId: "",
    itemName: "",
    itemCode: "",
    uom: "",

    qty: "0",
    price: "0",
    usageLimit: "1",

    totalValue: "0",
    approvedValue: "0",
    approvedQty: "0",

    // For Vehicle/Labour screen
    totalAmount: "0",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // -----------------------------
  // Dropdown data
  // -----------------------------
  const [projectList, setProjectList] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);

  const [itemList, setItemList] = useState([]);
  const [itemLoading, setItemLoading] = useState(false);

  // View/Edit loader
  const [loadingOne, setLoadingOne] = useState(false);
  const [loadedRow, setLoadedRow] = useState(null); // store rows[0]

  // Prevent one-time auto-clear issues when loading view/edit
  const hydratedRef = useRef(false);

  // -----------------------------
  // Load Projects (Project Name/Code)
  // -----------------------------
  useEffect(() => {
    const loadProjects = async () => {
      if (!branchid) return;

      setProjectLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/api/estimationListing/projects`, {
          params: { branchid },
          headers: commonHeaders,
        });

        if (res.data?.success) {
          setProjectList(res.data.list || []);
        } else {
          setProjectList([]);
        }
      } catch (e) {
        console.error(e);
        setProjectList([]);
      } finally {
        setProjectLoading(false);
      }
    };

    loadProjects();
  }, [branchid, commonHeaders]);

  useEffect(() => {
    if (!isView) return;
    if (!loadedRow) return;
    if (!projectList.length) return;

    // force select to re-render correctly
    setFormData((prev) => ({
      ...prev,
      projectId: String(loadedRow.projectId || prev.projectId || ""),
    }));
  }, [isView, loadedRow, projectList.length]);

  // -----------------------------
  // View/Edit: load full record by estimationid
  // -----------------------------
  const loadByEstimation = async () => {
    if (!estimationidFromUrl) return;

    setLoadingOne(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/estimationListing/internalEstimation/byEstimation`,
        {
          params: { estimationid: estimationidFromUrl, branchid },
          headers: commonHeaders,
        }
      );

      if (!res.data?.success || !res.data?.rows?.length) {
        toast.error("No details found for this estimation");
        return;
      }

      const first = res.data.rows[0];
      setLoadedRow(first);

      const materialType =
        first.flag === "R" ? "Raw Material" : first.flag === "V" ? "Vehicle" : "Labour";

      setFormData((prev) => ({
        ...prev,
        projectId: String(res.data.projectId || first.projectId || ""),
        projectName: res.data.projectName || "",
        projectCode: res.data.projectCode || "",
        materialType,

        itemId: String(first.itemId || ""),
        itemName: first.itemName || "",
        itemCode: first.itemCode || "",
        uom: first.uomName || "",

        qty: String(first.qty ?? 0),
        price: String(first.price ?? 0),
        usageLimit: String(first.usageLimit ?? 1),

        totalValue: String(first.originalValue ?? 0),
        approvedValue: String(first.estimatedValue ?? 0),
        approvedQty: String(first.approvedQty ?? 0),

        totalAmount: String(first.originalValue ?? 0),
      }));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Failed to load estimation");
    } finally {
      setLoadingOne(false);
    }
  };

  useEffect(() => {
    loadByEstimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimationidFromUrl, branchid, finYearId, finYear]);

  // -----------------------------
  // Project select (sync name + code)
  // -----------------------------
  const handleProjectSelect = (projectId) => {
    const pid = Number(projectId || 0);
    const found = projectList.find((p) => Number(p.projectId) === pid);

    // Clear item fields whenever project changes (NEW mode behavior)
    setItemList([]);
    setFormData((prev) => ({
      ...prev,
      projectId: pid || "",
      projectName: found ? String(found.projectName) : "",
      projectCode: found ? String(found.projectCode) : "",

      itemId: "",
      itemName: "",
      itemCode: "",
      uom: "",
      qty: "0",
      price: "0",
      usageLimit: "1",
      totalValue: "0",
      approvedValue: "0",
      approvedQty: "0",
      totalAmount: "0",
    }));
  };

  // If URL has projectid and you are in NEW mode, auto select project
  useEffect(() => {
    if (!isNew) return;
    if (!projectidFromUrl) return;
    if (!projectList.length) return;
    handleProjectSelect(projectidFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, projectidFromUrl, projectList.length]);

  // -----------------------------
  // Load Items only when:
  // - project selected
  // - materialType is Raw Material
  // For View/Edit we load list but DO NOT clear hydrated selection
  // -----------------------------
  useEffect(() => {
    const pid = Number(formData.projectId || 0);

    if (!pid || formData.materialType !== "Raw Material") {
      setItemList([]);
      return;
    }

    const loadItems = async () => {
      setItemLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/api/estimationListing/projectItems`, {
          params: { branchid, projectid: pid },
          headers: commonHeaders,
        });

        if (res.data?.success) {
          setItemList(res.data.list || []);
        } else {
          setItemList([]);
        }

        // ✅ IMPORTANT:
        // In NEW mode: clear selection after reload
        // In VIEW/EDIT: keep hydrated selection
        if (isNew) {
          setFormData((prev) => ({
            ...prev,
            itemId: "",
            itemName: "",
            itemCode: "",
            uom: "",
          }));
        }
      } catch (e) {
        console.error(e);
        setItemList([]);

        if (isNew) {
          setFormData((prev) => ({
            ...prev,
            itemId: "",
            itemName: "",
            itemCode: "",
            uom: "",
          }));
        }
      } finally {
        setItemLoading(false);
      }
    };

    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projectId, formData.materialType, branchid, isNew, commonHeaders]);

  // -----------------------------
  // Item select (sync name+code+uom)
  // -----------------------------
  const handleItemSelect = (itemId) => {
    const iid = Number(itemId || 0);
    const found = itemList.find((x) => Number(x.itemId) === iid);

    setFormData((prev) => ({
      ...prev,
      itemId: iid || "",
      itemName: found ? String(found.itemName) : "",
      itemCode: found ? String(found.itemCode) : "",
      uom: found ? String(found.uom || found.uomName || "") : "",
    }));
  };

  // -----------------------------
  // CALCULATIONS
  // -----------------------------
  useEffect(() => {
    if (isReadOnly) return;
    if (formData.materialType !== "Raw Material") return;

    const qty = Number(formData.qty || 0);
    const price = Number(formData.price || 0);
    let usage = Number(formData.usageLimit || 1);

    if (usage < 1) usage = 1;
    if (usage > 100) usage = 100;

    const totalValue = qty * price;
    const approvedValue = (totalValue * usage) / 100;
    const approvedQty = (qty * usage) / 100;

    setFormData((prev) => ({
      ...prev,
      usageLimit: String(usage),
      totalValue: totalValue.toFixed(2),
      approvedValue: approvedValue.toFixed(2),
      approvedQty: String(approvedQty),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.qty, formData.price, formData.usageLimit, formData.materialType, isView, editEnabled]);

  useEffect(() => {
    if (isReadOnly) return;
    if (formData.materialType === "Raw Material") return;

    const totalAmount = Number(formData.totalAmount || 0);
    let usage = Number(formData.usageLimit || 1);

    if (usage < 1) usage = 1;
    if (usage > 100) usage = 100;

    const approvedValue = (totalAmount * usage) / 100;

    setFormData((prev) => ({
      ...prev,
      usageLimit: String(usage),
      approvedValue: approvedValue.toFixed(2),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.totalAmount, formData.usageLimit, formData.materialType, isView, editEnabled]);

  // Usage limit warning
  useEffect(() => {
    if (isReadOnly) return;
    const u = Number(formData.usageLimit || 0);
    if (!u) return;
    if (u < 1 || u > 100) {
      toast.error("Please enter usage limit within 1-100");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.usageLimit, isView, editEnabled]);

  // -----------------------------
  // When material type changes
  // -----------------------------
  useEffect(() => {
    if (!isNew) return;

    if (formData.materialType === "Raw Material") {
      setFormData((prev) => ({
        ...prev,
        totalAmount: "0",
      }));
      return;
    }

    setItemList([]);
    setFormData((prev) => ({
      ...prev,
      itemId: "",
      itemName: "",
      itemCode: "",
      uom: "",
      qty: "0",
      price: "0",
      totalValue: "0",
      approvedQty: "0",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.materialType, isNew]);

  // -----------------------------
  // Update
  // -----------------------------
  const handleUpdate = async () => {
    try {
      const userid = Number(userId || 0);

      if (!branchid || !userid) {
        toast.error("branchId/userId missing");
        return;
      }
      if (!estimationidFromUrl || !loadedRow?.estDetId) {
        toast.error("estimationid / estDetId missing in page");
        return;
      }

      const payload = {
        branchid,
        userid,
        estimationid: estimationidFromUrl,
        estDetId: loadedRow.estDetId,
        projectid: Number(formData.projectId),

        materialType: formData.materialType,

        // RAW
        itemid: Number(formData.itemId || 0),
        qty: Number(formData.qty || 0),
        price: Number(formData.price || 0),
        usageLimit: Number(formData.usageLimit || 0),

        // VEH/LAB
        totalAmount: Number(formData.totalAmount || 0),
      };

      const res = await axios.put(
        `${BASE_URL}/api/estimationListing/update`,
        payload,
        {
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        toast.success(res.data.message || "Updated Successfully");
        setEditEnabled(false);
        await loadByEstimation();
      } else {
        toast.error(res.data?.message || "Update failed");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Server error");
    }
  };

  // -----------------------------
  // Save
  // -----------------------------
  const handleSave = async () => {
    try {
      const userid = Number(userId || 0);

      if (!branchid || !userid) {
        toast.error("branchId/userId missing");
        return;
      }

      if (!formData.projectId) {
        toast.error("Please Select Project");
        return;
      }

      if (isView) {
        toast.info("View mode: cannot save");
        return;
      }

      const payload = {
        branchid,
        userid,
        projectid: Number(formData.projectId),
        materialType: formData.materialType,

        // Raw
        itemid: Number(formData.itemId || 0),
        qty: Number(formData.qty || 0),
        price: Number(formData.price || 0),
        usageLimit: Number(formData.usageLimit || 0),
        totalValue: Number(formData.totalValue || 0),
        approvedValue: Number(formData.approvedValue || 0),
        approvedQty: Number(formData.approvedQty || 0),

        // Vehicle/Labour
        totalAmount: Number(formData.totalAmount || 0),
      };

      const res = await axios.post(
        `${BASE_URL}/api/estimationListing/internalEstimation/insert`,
        payload,
        {
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        toast.success(res.data.message || "Inserted Successfully");
        if (formData.materialType === "Raw Material") handleAddNew();
      } else {
        toast.error(res.data?.message || "Insert failed");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Server error");
    }
  };

  const handleAddNew = () => {
    // Clear item inputs only, keep project selected
    setFormData((prev) => ({
      ...prev,
      itemId: "",
      itemName: "",
      itemCode: "",
      uom: "",
      qty: "0",
      price: "0",
      usageLimit: "1",
      totalValue: "0",
      approvedValue: "0",
      approvedQty: "0",
    }));
    toast.info("Ready for next item");
  };

  const handleBack = () => navigate(-1);

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <ProjectPreOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">INTERNAL ESTIMATION</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className="flex items-end mb-4">
          <button
            onClick={handleBack}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
              isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
            }`}
          >
            <FaArrowLeft />
            Back
          </button>
        </div>

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Internal Estimation
            </h1>
          </div>

          {loadingOne && (
            <div className={`${isDark ? "text-gray-200" : "text-gray-700"} mb-4`}>
              Loading estimation...
            </div>
          )}

          <div className="space-y-6">
            {/* Project Details Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Project Name */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Project Name <span className="text-red-500">*</span>
                </label>

                                <select
                  value={String(formData.projectId || "")}
                  disabled={isReadOnly || isEdit}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">
                    {projectLoading ? "Loading..." : "Select"}
                  </option>

                  {(isView || isEdit) && formData.projectId && formData.projectName && (
                    <option value={String(formData.projectId)}>{formData.projectName}</option>
                  )}

                  {projectList.map((p) => (
                    <option key={p.projectId} value={String(p.projectId)}>
                      {p.projectName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project Code */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Project Code <span className="text-red-500">*</span>
                </label>

                                <select
                  value={String(formData.projectId || "")}
                  disabled={isReadOnly || isEdit}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">
                    {projectLoading ? "Loading..." : "Select"}
                  </option>

                  {(isView || isEdit) && formData.projectId && formData.projectCode && (
                    <option value={String(formData.projectId)}>{formData.projectCode}</option>
                  )}

                  {projectList
                    .slice()
                    .sort((a, b) => Number(a.projectCode) - Number(b.projectCode))
                    .map((p) => (
                      <option key={p.projectId} value={String(p.projectId)}>
                        {p.projectCode}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* ADD RAW MATERIALS / VEHICLES / LABOURS Section */}
            <div
              className={`p-4 rounded-lg border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-blue-50 border-blue-200"
              }`}
            >
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                ADD RAW MATERIALS / VEHICLES / LABOURS
              </h2>

              {/* Radio Buttons */}
              <div className="flex gap-6 mb-6">
                {["Raw Material", "Vehicle", "Labour"].map((t) => (
                  <label key={t} className={`flex items-center gap-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <input
                      type="radio"
                      name="materialType"
                      value={t}
                      disabled={isReadOnly || isEdit}
                      checked={formData.materialType === t}
                      onChange={(e) => handleInputChange("materialType", e.target.value)}
                      className="text-blue-600"
                    />
                    {t}
                  </label>
                ))}
              </div>

              {/* ✅ CONDITIONAL UI */}
              {formData.materialType === "Raw Material" ? (
                <>
                  {/* RAW MATERIAL SCREEN */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      {/* Item Name */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Item Name <span className="text-red-500">*</span>
                        </label>

                        <select
                          value={String(formData.itemId || "")}
                          disabled={!formData.projectId || isReadOnly}
                          onChange={(e) => handleItemSelect(e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          } ${!formData.projectId || isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          <option value="">
                            {!formData.projectId ? "Select Project First" : itemLoading ? "Loading..." : isView ? "—" : "Select"}
                          </option>

                          {(isView || isEdit) && formData.itemId && formData.itemName && (
                            <option value={String(formData.itemId)}>{formData.itemName}</option>
                          )}

                          {itemList.map((it) => (
                            <option key={it.itemId} value={String(it.itemId)}>
                              {it.itemName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Item Code */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Item Code <span className="text-red-500">*</span>
                        </label>

                        <select
                          value={String(formData.itemId || "")}
                          disabled={!formData.projectId || isReadOnly}
                          onChange={(e) => handleItemSelect(e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          } ${!formData.projectId || isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          <option value="">
                            {!formData.projectId ? "Select Project First" : itemLoading ? "Loading..." : isView ? "—" : "Select"}
                          </option>

                          {(isView || isEdit) && formData.itemId && formData.itemCode && (
                            <option value={String(formData.itemId)}>{formData.itemCode}</option>
                          )}

                          {itemList
                            .slice()
                            .sort((a, b) => String(a.itemCode).localeCompare(String(b.itemCode)))
                            .map((it) => (
                              <option key={it.itemId} value={String(it.itemId)}>
                                {it.itemCode}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* UOM */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          UOM
                        </label>
                        <input
                          type="text"
                          value={formData.uom}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          }`}
                          readOnly
                        />
                      </div>

                      {/* Qty */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Qty <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.qty}
                          disabled={isReadOnly}
                          onChange={(e) => handleInputChange("qty", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                        />
                      </div>

                      {/* Price */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Price <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.price}
                          disabled={isReadOnly}
                          onChange={(e) => handleInputChange("price", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                        />
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      {/* Usage Limit */}
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Usage Limit (%) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.usageLimit}
                          disabled={isReadOnly}
                          onChange={(e) => handleInputChange("usageLimit", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          } ${isView ? "opacity-60 cursor-not-allowed" : ""}`}
                        />
                      </div>

                      {/* Total Value */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Total Value <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.totalValue}
                          className={`w-full px-4 py-2 border rounded-lg ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          }`}
                          readOnly
                        />
                      </div>

                      {/* Approved Value */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Approved Value <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.approvedValue}
                          readOnly
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>

                      {/* Approved Qty */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Approved Qty <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.approvedQty}
                          readOnly
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-center items-center gap-6 pt-6">
                    {isView && !editEnabled && (
                      <button
                        onClick={() => setEditEnabled(true)}
                        className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                        }`}
                      >
                        <FaPlus />
                        Edit
                      </button>
                    )}

                    {isView && editEnabled && (
                      <>
                        <button
                          onClick={handleUpdate}
                          className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                            isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                          }`}
                        >
                          <FaSave />
                          Update
                        </button>

                        <button
                          onClick={() => {
                            setEditEnabled(false);
                            loadByEstimation();
                          }}
                          className="flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg bg-gray-500 hover:bg-gray-600 text-white"
                        >
                          Cancel
                        </button>
                      </>
                    )}

                    {!isView && (
                      <>
                        <button
                          onClick={handleSave}
                          className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                            isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                          }`}
                        >
                          <FaSave />
                          Save
                        </button>

                        <button
                          onClick={handleAddNew}
                          className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                            isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                          }`}
                        >
                          <FaPlus />
                          Add New
                        </button>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* VEHICLE / LABOUR SCREEN */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left */}
                    <div className="space-y-4">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Total Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.totalAmount}
                          disabled={isReadOnly}
                          onChange={(e) => handleInputChange("totalAmount", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Usage Limit (%) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.usageLimit}
                          disabled={isReadOnly}
                          onChange={(e) => handleInputChange("usageLimit", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                        />
                      </div>
                    </div>

                    {/* Right */}
                    <div className="space-y-4">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Approved Value <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.approvedValue}
                          readOnly
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center items-center gap-6 pt-6">
                    {isView && !editEnabled && (
                      <button
                        onClick={() => setEditEnabled(true)}
                        className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}
                      >
                        <FaPlus /> Edit
                      </button>
                    )}

                    {isView && editEnabled && (
                      <>
                        <button
                          onClick={handleUpdate}
                          className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}
                        >
                          <FaSave /> Update
                        </button>

                        <button
                          onClick={() => {
                            setEditEnabled(false);
                            loadByEstimation();
                          }}
                          className="flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg bg-gray-500 hover:bg-gray-600 text-white"
                        >
                          Cancel
                        </button>
                      </>
                    )}

                    {!isView && (
                      <>
                        <button
                          onClick={handleSave}
                          className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}
                        >
                          <FaSave /> Save
                        </button>

                        <button
                          onClick={handleAddNew}
                          className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}
                        >
                          <FaPlus /> Add New
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default InternalEstimation;